import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Início', href: '/' },
  { label: 'Portfólio', href: '/portfolio' },
  { label: 'Contato', href: '/contato' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const onScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !mobileOpen && !scrolled;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Background layer — white with blur, fades in/out (desktop only) */}
      <div
        className={[
          'absolute inset-0 bg-white/95 shadow-sm backdrop-blur-md',
          'transition-opacity duration-1000 ease-in-out',
          'hidden md:block',
          isTransparent ? 'opacity-0' : 'opacity-100',
        ].join(' ')}
      />
      {/* Mobile background — always visible so content never disappears */}
      <div
        className={[
          'absolute inset-0 bg-white/95 shadow-sm backdrop-blur-md',
          'transition-opacity duration-500 ease-in-out',
          'md:hidden',
          isTransparent ? 'opacity-0' : 'opacity-100',
        ].join(' ')}
      />

      <div className="relative flex items-center justify-between px-6 md:px-10 lg:px-16 h-16 md:h-20">
        {/* Logo */}
        <Link to="/">
          <div className="hidden sm:flex flex-col">
            <span
              className={[
                'font-serif text-lg md:text-xl font-medium tracking-wide leading-tight',
                'transition-colors duration-[1200ms] ease-in-out',
                isTransparent ? 'text-white' : 'text-dark',
              ].join(' ')}
            >
              Jéssica Bonini
            </span>
            <span
              className={[
                'text-[10px] md:text-xs tracking-[0.25em] uppercase',
                'transition-colors duration-[1200ms] ease-in-out',
                isTransparent ? 'text-white' : 'text-dark',
              ].join(' ')}
            >
              Fotógrafa
            </span>
          </div>
          <span
            className={[
              'sm:hidden font-serif text-lg font-medium tracking-wide leading-tight',
              'transition-colors duration-[1200ms] ease-in-out',
              'text-dark',
            ].join(' ')}
          >
            JB
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={[
                'text-sm font-medium tracking-wide relative group',
                'transition-colors duration-[1200ms] ease-in-out',
                isTransparent ? 'text-white' : 'text-dark',
              ].join(' ')}
            >
              {link.label}
              <span
                className={[
                  'absolute -bottom-1 left-0 h-[1.5px] w-0 group-hover:w-full',
                  'transition-all duration-300',
                  isTransparent ? 'bg-white' : 'bg-warm-700',
                ].join(' ')}
              />
            </Link>
          ))}
        </nav>

        {/* CTA button */}
        <Link
          to="/contato"
          className={[
            'hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap',
            'transition-colors duration-[1200ms] ease-in-out',
            isTransparent ? 'bg-white text-dark' : 'bg-dark text-white',
          ].join(' ')}
        >
          Orçamento
          <i className="ri-arrow-right-line text-xs transition-colors duration-[1200ms] ease-in-out" />
        </Link>

        {/* Mobile toggle — always white bg + dark text so it's visible on ANY background */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white text-dark shadow-md border border-warm-200"
          aria-label="Menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
        </button>
      </div>

      {/* Mobile menu — z-50 e shadow forte para nunca sumir */}
      {mobileOpen && (
        <div className="md:hidden relative z-50 bg-white border-t border-warm-100 px-6 py-6 flex flex-col gap-4 shadow-xl">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={[
                'text-base font-medium py-2',
                'transition-colors duration-[1200ms] ease-in-out',
                location.pathname === link.href ? 'text-warm-700' : 'text-dark',
              ].join(' ')}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contato"
            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium bg-dark text-white"
          >
            Solicitar Orçamento
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      )}
    </header>
  );
}